// @ts-nocheck
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  StyledContainer,
  StyledHeader,
  StyledTableContainer,
  StyledWhiteTableCell,
  StyledLightGreyTableHeader,
  StyledGreyTableCell,
  StyledWhiteTableHeader,
  StyledGridChild,
  StyledGridChildSeperator,
  StyledWhiteCheck,
  StyledLightGreyCheck,
  StyledBodyTableCell,
  StyledStack,
  StyledWrapper
} from "../Options/Options.styles";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableHead
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/shared/Button/Button";
import { snakeToCamel, camelFromLabel, AddEncounterEvent } from "helpers/variables";
import { GlobalContext } from "context/Provider";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import Session from "helpers/session";
import API from "api";
import { MEASUREMENT_KEY_MAP } from "helpers/variables";

interface AttributeProps {
    memberId: string;
}

export default function Options({memberId}: AttributeProps) {
  const {
    config,
    configDispatch,
    encounterDispatch,
    encounters,
    member,
    memberDispatch,
    encounterTimer,
    settings,
    settingsDispatch
  } = useContext(GlobalContext);
  const { t } = useTranslation();
  const optionsRows = [
    "activity",
    "sleep",
    "blood-glucose",
    "blood-pressure",
    "heart-rate",
    "respiration-rate",
    "oxygen-saturation",
    "spirometry",
    { label: ["FEV1", "FVC", "FEV1/FVC", "PEF"] },
    "temperature",
    "weight",
    "member-notes"
  ];
  const [options, setOptions] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [changes, setChanges] = useState(true);
  const [showSuccess, setSuccess] = useState<string>("");
  const [spirometryVisibility, setSpirometryVisibility] = useState({
    fev1: true,
    fvc: true,
    fev1fvc: true,
    pef: true,
  });
  const defaultSettings =
    settings?.default?.data?.system?.settings as
      | Record<string, Record<string, boolean>>
      | undefined;

  const isMeasurementDefaultVisible = (row: string | { label: string[] }) => {
    if (!defaultSettings) return true;

    if (typeof row === "string") {
      const key = MEASUREMENT_KEY_MAP[row] || row;
      const group = defaultSettings[key];
      return group ? Object.values(group).some(Boolean) : true;
    } else {
      const key = MEASUREMENT_KEY_MAP["spirometry"] || "spirometry";
      const group = defaultSettings[key];
      if (!group) return true;
      return row.label.some((child) => group[child.replace("/", "_")] !== false);
    }
  };

  const checkboxChange = (event: ChangeEvent<any>): void => {
    setChanges(false);
    setSuccess("");
    const id = event.target.id;
    const chartKey = id.replace("chart-", "");

    if (["fev1", "fvc", "fev1fvc", "pef"].includes(chartKey)) {
      setSpirometryVisibility((prev) => ({
        ...prev,
        [chartKey]: event.target.checked,
      }));
    } else {
      setOptions({ ...options, [id]: event.target.checked });
    }
  };

  const checkAllChange = (event: ChangeEvent<any>): void => {
    const isChecked = event.target.checked;
    const checkboxType = event.target.id;

    let finalAll: Record<string, boolean> = {};
    setChanges(false);
    setSuccess("");

    optionsRows.forEach((opt) => {
      if (typeof opt === "string") {
        if (!isMeasurementDefaultVisible(opt)) return;
        finalAll[`${checkboxType}${snakeToCamel(opt)}`] = isChecked;
      } else {
        opt.label.forEach((child) => {
          if (!isMeasurementDefaultVisible([child])) return;
          finalAll[`${checkboxType}${camelFromLabel(child)}`] = isChecked;
        });
      }
    });

    setOptions({ ...options, ...finalAll });
  };

  const submitOptions = () => {
    let finalHistoryOptions = {};
    let finalVitalTileOptions = {};
    let finalChartOptions = {};
    let finalSpirometryVisibility = {};
    for (let key in options) {
      const newKey = key.split("-");
      if (newKey[0] === "historyMenu") {
        finalHistoryOptions[newKey[1]] = options[key];
      }
      if (newKey[0] === "vitalTiles") {
        finalVitalTileOptions[newKey[1]] = options[key];
      }
      if (newKey[0] === "chart") {
        finalChartOptions[newKey[1]] = options[key];
      }
    }
    finalSpirometryVisibility = { ...spirometryVisibility };

    settingsDispatch({
      type: "UPDATE_MEMBER_SETTINGS",
      payload: {
        historyOptions: finalHistoryOptions,
        vitalTileOptions: finalVitalTileOptions,
        chartOptions: finalChartOptions,
        spirometryVisibility: finalSpirometryVisibility
      }
    });

    setChanges(true);
    setSuccess("success");
    const encounterEvent = AddEncounterEvent(
      "profile change",
      "Display options updated"
    );
    const duration = encounterTimer?.current?.duration ?? 0;
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("encounter") || "",
      encounterEvent,
      duration.toString() || ""
    );
  };

  useEffect(() => {
    API.kore.settings
      .update("?entity=user", settings?.member?.data, Session.crackCookieToken("coordinator"), memberId)
      .catch((err: any) => console.log("Error updating client settings"));
  }, [settings?.member?.data]);

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    let checkBoxObj = {};
    for (let key in settings?.member?.data?.system?.settings?.historyOptions) {
      checkBoxObj[`historyMenu-${key}`] = settings?.member?.data?.system?.settings?.historyOptions[key];
    }
    for (let key in settings?.member?.data?.system?.settings?.vitalTileOptions) {
      checkBoxObj[`vitalTiles-${key}`] = settings?.member?.data?.system?.settings?.vitalTileOptions[key];
    }
    for (let key in settings?.member?.data?.system?.settings?.chartOptions) {
      checkBoxObj[`chart-${key}`] = settings?.member?.data?.system?.settings?.chartOptions[key];
    }

    const savedSpirometry =
      settings?.member?.data?.system?.settings?.spirometryVisibility;
    const spirometryFields = ["FEV1", "FVC", "FEV1/FVC", "PEF"];
    const initialSpirometry: Record<string, boolean> = {};
    spirometryFields.forEach((field) => {
      const key = camelFromLabel(field);
      initialSpirometry[key] = savedSpirometry?.[key] ?? true;
    });

    setOptions(checkBoxObj);
    setSpirometryVisibility(initialSpirometry);
    setTimeout(() => setLoaded(true), 100);

  }, []);

  return (
    <StyledContainer>
      <Grid container>
        <Grid item sm={0} md={1.5} lg={0.5} />
        <Grid item sm={12} md={10} lg={11}>
          <StyledHeader>
            <h2>{t("u-display-options")}</h2>
          </StyledHeader>
          <StyledWrapper>
            <EncounterTimerDisplay/>
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledWhiteTableCell align={"center"} colSpan={5}>
                      <h4>{t("u-measurement-type")}</h4>
                    </StyledWhiteTableCell>
                    <StyledGreyTableCell align={"center"} colSpan={7}>
                      <h4>{t("u-show-in")}</h4>
                      <Grid container>
                        <StyledLightGreyTableHeader item xs={3.02}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id={"historyMenu-"}
                                onChange={checkAllChange}
                              />
                            }
                            label={"History Side Bar"}
                            sx={{ mt: 2, ml: 1 }}
                          />
                        </StyledLightGreyTableHeader>
                        <StyledWhiteTableHeader item xs={8.98}>
                          <h4>{t("u-dashboard")}</h4>
                          <Grid container>
                            <StyledGridChild item xs={6.65}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    id={"vitalTiles-"}
                                    onChange={checkAllChange}
                                  />
                                }
                                label={"Vitals Tile"}
                              />
                            </StyledGridChild>
                            <StyledGridChildSeperator item xs={5.35}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    id={"chart-"}
                                    onChange={checkAllChange}
                                  />
                                }
                                label={t("u-as-chart")}
                              />
                            </StyledGridChildSeperator>
                          </Grid>
                        </StyledWhiteTableHeader>
                      </Grid>
                    </StyledGreyTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {optionsRows.map((row) => {
                    if (!isMeasurementDefaultVisible(row)) return null;

                    if (typeof row === "string") {
                      return (
                        <TableRow key={row.toLowerCase().replace(" ", "-") + "-options"}>
                          <StyledBodyTableCell align="left" colSpan={5} sx={{ pl: "20px" }}>
                            {t(`u-${row}`)}
                          </StyledBodyTableCell>
                          <StyledBodyTableCell align="center" colSpan={7}>
                            <Grid container>
                              <StyledLightGreyCheck item xs={3}>
                                {loaded && (
                                  <Checkbox
                                    id={`historyMenu-${snakeToCamel(row)}`}
                                    checked={options[`historyMenu-${snakeToCamel(row)}`]}
                                    onChange={checkboxChange}
                                  />
                                )}
                              </StyledLightGreyCheck>
                              <StyledWhiteCheck item xs={5}>
                                {loaded && (
                                  <Checkbox
                                    id={`vitalTiles-${snakeToCamel(row)}`}
                                    checked={options[`vitalTiles-${snakeToCamel(row)}`]}
                                    onChange={checkboxChange}
                                  />
                                )}
                              </StyledWhiteCheck>
                              <StyledWhiteCheck item xs={4}>
                                {loaded && (
                                  <Checkbox
                                    id={`chart-${snakeToCamel(row)}`}
                                    checked={options[`chart-${snakeToCamel(row)}`]}
                                    onChange={checkboxChange}
                                  />
                                )}
                              </StyledWhiteCheck>
                            </Grid>
                          </StyledBodyTableCell>
                        </TableRow>
                      );
                    } else {
                      return row.label
                        .filter((child) => {
                          const group = defaultSettings["spir"];
                          if (!group) return true;
                          const key = child.replace("/", "_");
                          return group[key] !== false;
                        })
                        .map((child: string) => (
                        <TableRow key={`spirometry-${child}-options`}>
                          <StyledBodyTableCell align="left" colSpan={5} sx={{ pl: "60px" }}>
                            {child}
                          </StyledBodyTableCell>
                          <StyledBodyTableCell align="center" colSpan={7}>
                            <Grid container>
                              <StyledWhiteCheck item xs={8} sx={{ pt: "10px" }}>
                                Toggles visibility of charts and threshold inputs
                              </StyledWhiteCheck>
                              <StyledWhiteCheck item xs={4}>
                                <Checkbox
                                  id={`chart-${camelFromLabel(child)}`}
                                  checked={!!spirometryVisibility[camelFromLabel(child)]}
                                  onChange={checkboxChange}
                                />
                              </StyledWhiteCheck>
                            </Grid>
                          </StyledBodyTableCell>
                        </TableRow>
                      ));
                    }
                  })}
                </TableBody>
              </Table>
            </StyledTableContainer>
            <StyledStack>
              <Button
                variant="success"
                onClick={submitOptions}
                disabled={changes}
              >
                {t("u-apply")}
              </Button>
              {showSuccess === "success" ? (
                <Alert severity="success">
                  Your configuration changes have been saved
                </Alert>
              ) : (
                ""
              )}
            </StyledStack>
          </StyledWrapper>
        </Grid>
        <Grid item xs={0.5} />
      </Grid>
    </StyledContainer>
  );
}
